import { defineComponent, computed, ref } from 'vue'
import {
  transImageResource,
  type ImageOption,
  type VideoOption,
} from '@xmov/oss'
import { videoType } from './constant'
import { useMountedWatch } from '~/hooks/useMountedWatch'

const props = {
  lazy: {
    type: Boolean,
    default: true,
  },
  src: {
    type: String,
    required: false,
  },
  muted: {
    type: Boolean,
  },
  autoplay: {
    type: Boolean,
  },
  loop: {
    type: Boolean,
  },
  poster: {
    type: [Number, String],
  },
  preload: {
    type: String,
  },
  controls: {
    type: Boolean,
  },
  resourceOption: {
    type: Object as PropType<ImageOption | VideoOption>,
  },
  transformResource: {
    type: Function as PropType<(url: string) => string>,
  },
}

// type PropsType = ExtractPropTypes<typeof props>

export default defineComponent({
  name: 'Resource',
  props,
  emits: ['ended', 'play', 'pause'],
  setup(props, { expose, emit, attrs }) {
    const mediaRef = ref()
    const isPlaying = ref(false)
    const poster = ref('')

    expose({
      pause() {
        mediaRef.value?.pause?.()
      },
      play() {
        mediaRef.value?.play?.()
      },
      mute(_muted: boolean) {
        mediaRef.value.muted = !_muted
      },
      get el() {
        return mediaRef.value
      },
    })

    const mediaType = computed(() => {
      if (!props.src) {
        return 'img'
      }

      const arr = props.src.split('.')
      const ext = arr[arr.length - 1]
      const extLowercase = ext?.toLowerCase?.() || ''

      // if (picType.includes(extLowercase)) {
      //   return 'img'
      // }
      if (videoType.includes(extLowercase)) {
        return 'video'
      }

      return 'img'
    })

    function getSrc() {
      if (props.transformResource) {
        return props.transformResource(props.src!)
      }

      return mediaType.value === 'img'
        ? transImageResource(props.src, props.resourceOption as ImageOption)
        : props.src
    }

    const src = ref(getSrc())
    const isBreak = ref(false)

    onMounted(() => {
      src.value = getSrc()
    })

    function ended() {
      emit('ended')
    }

    function onVideoClick() {
      if (props.autoplay) {
        return
      }

      if (!isPlaying.value) {
        mediaRef.value?.play?.()
        onPlay()
      } else {
        mediaRef.value?.pause?.()
        onPause()
      }
    }

    function onPlay() {
      isPlaying.value = true
      emit('play')
    }

    function onPause() {
      isPlaying.value = false
      emit('pause')
    }

    function setVideoPoster() {
      if (mediaType.value === 'img' || typeof props.poster !== 'number') {
        return
      }

      const video = mediaRef.value as HTMLVideoElement
      // 创建一个Canvas元素
      const canvas = document.createElement('canvas')
      canvas.width = video.clientWidth
      canvas.height = video.clientHeight
      const context = canvas.getContext('2d')

      function setPoster() {
        // 在视频的第10帧时，将该帧绘制到Canvas上
        context!.drawImage(video, 0, 0, canvas.width, canvas.height)
        // 将Canvas的内容作为封面设置给视频
        poster.value = canvas.toDataURL('image/jpeg')

        video.removeEventListener('seeked', setPoster)
      }

      if (video.readyState < 2) {
        // 监听视频加载事件
        video.addEventListener('loadeddata', function () {
          // 移动到第10帧（注意：视频是异步加载的，确保视频已加载）

          // 监听视频帧可用事件
          video.addEventListener('seeked', setPoster)
          video.currentTime = (props.poster as number) - 1
        })
      } else {
        video.currentTime = (props.poster as number) - 1

        // 监听视频帧可用事件
        video.addEventListener('seeked', setPoster)
      }
    }

    watch(
      () => props.src,
      () => {
        if (props.src) {
          // 这里可能会有问题
          src.value = getSrc()
        }
      },
      {
        immediate: true,
      }
    )

    useMountedWatch(
      () => props.src,
      () => {
        nextTick(setVideoPoster)
      }
    )

    function handleImageLoadError() {
      src.value = transformImage(
        getFeUrl('/youyan-platform/png/home/img-placeholder-2x.png')
      )
      isBreak.value = true
    }

    return () => {
      const commonProps = {
        class: attrs.class,
      }

      if (mediaType.value === 'video') {
        const videoProps: any = {
          muted: props.muted,
          loop: props.loop,
          autoplay: props.autoplay,
          preload: props.preload,
          controls: props.controls,
          ...commonProps,
        }

        if (typeof props.poster === 'string') {
          videoProps.poster = props.poster
        } else if (typeof props.poster === 'number') {
          videoProps.poster = poster.value
        }

        return (
          <video
            {...videoProps}
            loading="lazy"
            src={src.value}
            ref={mediaRef}
            onEnded={ended}
            onPlay={onPlay}
            onPause={onPause}
            onClick={onVideoClick}
            name="resourceVideo"
            crossorigin="anonymous"
            {...attrs}
          ></video>
        )
      }
      if (mediaType.value === 'img') {
        const imgProps: any = {
          ...commonProps,
        }

        if (props.lazy !== false) {
          imgProps.loading = 'lazy'
        }

        if (isBreak.value) {
          Object.assign(imgProps, {
            style: {
              objectFit: 'scale-down !important',
            },
          })
        }

        return (
          <img
            onError={handleImageLoadError}
            {...imgProps}
            src={src.value}
            ref={mediaRef}
            {...attrs}
          ></img>
        )
      }
    }
  },
})
